import { BoardMarkerPill, boardMarkerPillWidth } from "../BoardMarkerPill";
import { LabeledCircle } from "../LabeledCircle";
import { resolveSourceConfidence } from "./utils";
import {
  CONFIDENT_SOURCE_FILL,
  CONFIDENT_SOURCE_STROKE,
  ESTIMATED_SOURCE_FILL,
  ESTIMATED_SOURCE_STROKE,
  ESTIMATED_BADGE_FILL,
  RISER_BADGE_FILL,
  BADGE_TEXT_FILL,
  HIGHLIGHT_COLOR,
  WALL_COLOR,
  SELECTED_WALL_STROKE,
  REMOVED_MARKER_FILL,
  REMOVED_MARKER_STROKE,
  CANDIDATE_DEVICE_STROKE,
  SELECTED_DEVICE_STROKE,
  UNROUTED_MARKER_CASING,
  UNROUTED_MARKER_STROKE,
  SOURCE_MARKER_OPACITY_AT_HIGH_ZOOM,
  BADGE_OPACITY_AT_HIGH_ZOOM,
  HEIGHT_MARK_FILL,
  HEIGHT_MARK_CASING,
} from "./constants";
import type { CableTakeoffMarkersProps } from "./types";

const polygonCentre = (points: { x: number; y: number }[]) => ({
  x: points.reduce((sum, at) => sum + at.x, 0) / points.length,
  y: points.reduce((sum, at) => sum + at.y, 0) / points.length,
});

// Cable-takeoff-specific overlays drawn on top of the circuit routes: source
// (board) markers with a confidence badge, a blinking highlight, and crossed-out
// markers for removed devices.
export const CableTakeoffMarkers = ({
  ghostMarker,
  boardMarkers,
  removedMarkers,
  unroutedDeviceMarkers,
  highlightMarker,
  heightMarks,
  candidateDeviceMarkers,
  selectedDeviceMarkers,
  selectedDeviceMarkersBlink,
  zoneDraft,
  feedAreas,
  planWalls,
  wallDraft,
  wallSelection,
  size,
  isLowZoom,
}: CableTakeoffMarkersProps) => {
  return (
    <>
      {/* Walls, and the openings in them. NEVER in a cable colour and heavier
          than any of them: a wall is the one thing on this overlay that is not
          a cable, and it is the reason a run goes the long way round. An opening
          is drawn as the gap it is — white through the wall, then a thin outline,
          so a door reads as a door and not as a shorter wall. */}
      {planWalls?.map((one) => {
        if (one.points.length < 2) return null;
        const isWall = one.kind === "wall";
        // A wall is its outline, closed; a door is the line across its wall.
        const d =
          one.points
            .map((at, index) => `${index === 0 ? "M" : "L"} ${at.x} ${at.y}`)
            .join(" ") + (isWall ? " Z" : "");
        const isSelected = wallSelection?.selectedWallId === one.id;
        return (
          <g key={one.id} data-testid={`plan-wall-mark-${one.id}`}>
            {isSelected && (
              <path
                pointerEvents="none"
                d={d}
                fill="none"
                stroke={SELECTED_WALL_STROKE}
                strokeOpacity={0.45}
                strokeWidth={size * 1.4}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {isWall ? (
              <path
                data-testid={`plan-wall-body-${one.id}`}
                pointerEvents="none"
                d={d}
                fill={WALL_COLOR}
                fillOpacity={0.85}
                stroke={WALL_COLOR}
                strokeWidth={size * 0.1}
                strokeLinejoin="miter"
              />
            ) : (
              <g pointerEvents="none">
                <path d={d} fill="none" stroke="#ffffff" strokeWidth={size * 0.5} />
                <path
                  d={d}
                  fill="none"
                  stroke={WALL_COLOR}
                  strokeWidth={size * 0.1}
                  strokeDasharray={`${size * 0.3} ${size * 0.3}`}
                />
              </g>
            )}
            {wallSelection && (
              // All three events are his: mouseup alone falls through to the panel's map click.
              <path
                data-testid={`plan-wall-target-${one.id}`}
                d={d}
                fill="transparent"
                stroke="transparent"
                strokeWidth={size * 1.6}
                pointerEvents="all"
                style={{ cursor: "pointer" }}
                onMouseDown={(event) => event.stopPropagation()}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  wallSelection.onSelect(one.id);
                }}
              />
            )}
          </g>
        );
      })}

      {/* What he is drawing right now.
          Three things, and the RUBBER BAND is the one that matters: the corners
          he has clicked are history, and what he needs to see is the length he
          is about to draw. Without it he clicks blind — the wall appears only
          after the click that placed it. */}
      {wallDraft && (
        <g pointerEvents="none">
          {wallDraft.points.length > 1 && (
            <path
              d={wallDraft.points
                .map(
                  (at, index) => `${index === 0 ? "M" : "L"} ${at.x} ${at.y}`,
                )
                .join(" ")}
              fill="none"
              stroke={WALL_COLOR}
              strokeWidth={size * 0.4}
              strokeOpacity={0.55}
              strokeDasharray={`${size * 0.6} ${size * 0.4}`}
            />
          )}
          {/* From the last corner to the pointer: the wall he gets if he clicks
              now. Lighter than the placed lengths, because it is not placed. */}
          {wallDraft.cursor && wallDraft.points.length > 0 && (
            <line
              x1={wallDraft.points[wallDraft.points.length - 1].x}
              y1={wallDraft.points[wallDraft.points.length - 1].y}
              x2={wallDraft.cursor.x}
              y2={wallDraft.cursor.y}
              stroke={WALL_COLOR}
              strokeWidth={size * 0.3}
              strokeOpacity={0.35}
              strokeDasharray={`${size * 0.35} ${size * 0.3}`}
            />
          )}
          {wallDraft.points.map((at, index) => (
            <circle
              key={index}
              cx={at.x}
              cy={at.y}
              r={size * 0.28}
              fill="#ffffff"
              stroke={WALL_COLOR}
              strokeWidth={size * 0.1}
            />
          ))}
          {/* Where the next corner lands, before there is any corner to band
              from — which is all he has to go on for the first click. A CROSS,
              not a disc: a disc on this layer is a device. */}
          {wallDraft.cursor && (
            <g
              stroke={WALL_COLOR}
              strokeWidth={size * 0.12}
              strokeOpacity={0.9}
            >
              <line
                x1={wallDraft.cursor.x - size * 0.7}
                y1={wallDraft.cursor.y}
                x2={wallDraft.cursor.x + size * 0.7}
                y2={wallDraft.cursor.y}
              />
              <line
                x1={wallDraft.cursor.x}
                y1={wallDraft.cursor.y - size * 0.7}
                x2={wallDraft.cursor.x}
                y2={wallDraft.cursor.y + size * 0.7}
              />
            </g>
          )}
        </g>
      )}

      {feedAreas?.map((area) => {
        if (area.points.length < 3) return null;
        const centre = polygonCentre(area.points);
        return (
          <g
            key={area.id}
            pointerEvents="none"
            opacity={area.isFocused === false ? 0.45 : 1}
          >
            <polygon
              points={area.points.map((at) => `${at.x},${at.y}`).join(" ")}
              fill={HIGHLIGHT_COLOR}
              fillOpacity={area.isFocused ? 0.12 : 0.05}
              stroke={HIGHLIGHT_COLOR}
              strokeOpacity={area.isFocused ? 0.9 : 0.5}
              strokeWidth={size * (area.isFocused ? 0.16 : 0.08)}
              strokeDasharray={`${size * 0.9} ${size * 0.5}`}
            />
            {!isLowZoom && (
              <text
                x={centre.x}
                y={centre.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={size * 1.1}
                fontWeight={700}
                fill={HIGHLIGHT_COLOR}
                opacity={0.75}
              >
                {area.label}
              </text>
            )}
          </g>
        );
      })}
      {zoneDraft && (
        <g pointerEvents="none">
          {zoneDraft.points.length > 1 && (
            <polyline
              points={zoneDraft.points.map((at) => `${at.x},${at.y}`).join(" ")}
              fill={zoneDraft.points.length > 2 ? HIGHLIGHT_COLOR : "none"}
              fillOpacity={0.08}
              stroke={HIGHLIGHT_COLOR}
              strokeWidth={size * 0.14}
            />
          )}
          {zoneDraft.cursor && zoneDraft.points.length > 0 && (
            <line
              x1={zoneDraft.points[zoneDraft.points.length - 1].x}
              y1={zoneDraft.points[zoneDraft.points.length - 1].y}
              x2={zoneDraft.cursor.x}
              y2={zoneDraft.cursor.y}
              stroke={HIGHLIGHT_COLOR}
              strokeOpacity={0.5}
              strokeWidth={size * 0.1}
              strokeDasharray={`${size * 0.35} ${size * 0.3}`}
            />
          )}
          {zoneDraft.points.map((at, index) => (
            <circle
              key={index}
              cx={at.x}
              cy={at.y}
              // The first corner is where a click closes the zone, so it is the one he aims for.
              r={size * (index === 0 && zoneDraft.points.length > 2 ? 0.45 : 0.25)}
              fill="#ffffff"
              stroke={HIGHLIGHT_COLOR}
              strokeWidth={size * 0.1}
            />
          ))}
        </g>
      )}
      {ghostMarker && (
        <g pointerEvents="none">
          <circle
            cx={ghostMarker.x}
            cy={ghostMarker.y}
            r={size * 1.6}
            fill={HIGHLIGHT_COLOR}
            opacity={0.15}
          />
          <LabeledCircle
            x={ghostMarker.x}
            y={ghostMarker.y}
            radius={size * 0.85}
            label={ghostMarker.label}
            fillColor={ESTIMATED_SOURCE_FILL}
            strokeColor={HIGHLIGHT_COLOR}
            opacity={0.9}
          />
        </g>
      )}
      {boardMarkers?.map((marker, index) => {
        const confidence = resolveSourceConfidence(marker);
        const isEstimated = confidence === "estimated";
        const isRiser = confidence === "riser";
        const badgeX =
          marker.x + boardMarkerPillWidth({ label: marker.label, size }) / 2;
        return (
          <g key={`board-marker-${index}`}>
            <BoardMarkerPill
              x={marker.x}
              y={marker.y}
              size={size}
              fillColor={
                isEstimated ? ESTIMATED_SOURCE_FILL : CONFIDENT_SOURCE_FILL
              }
              strokeColor={
                isEstimated ? ESTIMATED_SOURCE_STROKE : CONFIDENT_SOURCE_STROKE
              }
              label={marker.label}
              opacity={isLowZoom ? 1 : SOURCE_MARKER_OPACITY_AT_HIGH_ZOOM}
              isHighlighted={marker.isHighlighted}
            />
            {isEstimated && (
              // Position invented at the device centroid → rose "!" warning.
              <g opacity={isLowZoom ? 1 : BADGE_OPACITY_AT_HIGH_ZOOM}>
                <circle
                  cx={badgeX}
                  cy={marker.y - size * 0.95}
                  r={size * 0.55}
                  fill={ESTIMATED_BADGE_FILL}
                  stroke={BADGE_TEXT_FILL}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={badgeX}
                  y={marker.y - size * 0.95}
                  fill={BADGE_TEXT_FILL}
                  fontSize={size * 0.8}
                  fontWeight="bold"
                  fontFamily="Arial"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  !
                </text>
              </g>
            )}
            {isRiser && (
              // Panel is known but this dot marks the riser entry, not the
              // panel itself → calm blue "i" info badge (not the rose warning).
              <g opacity={isLowZoom ? 1 : BADGE_OPACITY_AT_HIGH_ZOOM}>
                <circle
                  cx={badgeX}
                  cy={marker.y - size * 0.95}
                  r={size * 0.55}
                  fill={RISER_BADGE_FILL}
                  stroke={BADGE_TEXT_FILL}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={badgeX}
                  y={marker.y - size * 0.95}
                  fill={BADGE_TEXT_FILL}
                  fontSize={size * 0.8}
                  fontWeight="bold"
                  fontFamily="Arial"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  i
                </text>
              </g>
            )}
          </g>
        );
      })}
      {highlightMarker && (
        <g style={{ pointerEvents: "none" }}>
          <circle
            cx={highlightMarker.x}
            cy={highlightMarker.y}
            r={size * 0.6}
            fill={HIGHLIGHT_COLOR}
            opacity={0.9}
          />
          <circle
            cx={highlightMarker.x}
            cy={highlightMarker.y}
            r={size}
            fill="none"
            stroke={HIGHLIGHT_COLOR}
            strokeWidth={size * 0.25}
          >
            <animate
              attributeName="r"
              values={`${size};${size * 2.2};${size}`}
              dur="1s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.9;0;0.9"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </g>
      )}
      {heightMarks?.map((mark, index) => {
        const half = size * 0.9;
        const tip = mark.kind === "rise" ? -half : half;
        const points = `${mark.x},${mark.y + tip} ${mark.x - half},${mark.y - tip} ${mark.x + half},${mark.y - tip}`;
        return (
          <polygon
            key={`height-${index}`}
            data-testid={`height-mark-${mark.kind}`}
            points={points}
            fill={HEIGHT_MARK_FILL}
            stroke={HEIGHT_MARK_CASING}
            strokeWidth={size * 0.25}
            strokeLinejoin="round"
            style={{ pointerEvents: "none" }}
          />
        );
      })}
      {removedMarkers?.map((marker, index) => {
        const radius = size * 0.7;
        const crossOffset = radius * 0.55;
        return (
          <g
            key={`removed-marker-${index}`}
            opacity={0.95}
            style={{ pointerEvents: "none" }}
          >
            <circle
              cx={marker.x}
              cy={marker.y}
              r={radius}
              fill={REMOVED_MARKER_FILL}
              stroke={REMOVED_MARKER_STROKE}
              strokeWidth={radius * 0.28}
            />
            <line
              x1={marker.x - crossOffset}
              y1={marker.y - crossOffset}
              x2={marker.x + crossOffset}
              y2={marker.y + crossOffset}
              stroke={REMOVED_MARKER_STROKE}
              strokeWidth={radius * 0.28}
              strokeLinecap="round"
            />
            <line
              x1={marker.x + crossOffset}
              y1={marker.y - crossOffset}
              x2={marker.x - crossOffset}
              y2={marker.y + crossOffset}
              stroke={REMOVED_MARKER_STROKE}
              strokeWidth={radius * 0.28}
              strokeLinecap="round"
            />
          </g>
        );
      })}
      {unroutedDeviceMarkers?.map((marker, index) => {
        const half = size * 0.62;
        const side = half * 2;
        return (
          // The overlay is pointer-events:none and a title only shows on an element that takes them; the plan's handlers read the container rect, never the target, so clicks still land.
          <g key={`unrouted-device-${index}`} style={{ pointerEvents: "all" }}>
            {/* Per-device, so nothing on the drawing needs a legend read first. */}
            <title data-testid="unrouted-device-title">{marker.label}</title>
            <rect
              data-testid="unrouted-device-casing"
              x={marker.x - half}
              y={marker.y - half}
              width={side}
              height={side}
              fill="none"
              stroke={UNROUTED_MARKER_CASING}
              strokeWidth={size * 0.34}
            />
            <rect
              data-testid="unrouted-device-marker"
              x={marker.x - half}
              y={marker.y - half}
              width={side}
              height={side}
              fill="none"
              stroke={UNROUTED_MARKER_STROKE}
              strokeWidth={size * 0.18}
            />
          </g>
        );
      })}
      {/* What the pick is OFFERING him. Neutral and pulsing, under the rings:
          it says "these are the ones this click means", not "these are chosen". */}
      {candidateDeviceMarkers?.map((marker, index) => (
        <circle
          key={`candidate-device-${index}`}
          className="karl-blink"
          pointerEvents="none"
          cx={marker.x}
          cy={marker.y}
          r={size * 0.8}
          fill="none"
          stroke={CANDIDATE_DEVICE_STROKE}
          strokeWidth={size * 0.16}
          strokeDasharray={`${size * 0.34} ${size * 0.26}`}
        />
      ))}
      {selectedDeviceMarkers?.map((marker, index) => (
        <g
          key={`selected-device-${index}`}
          className={selectedDeviceMarkersBlink ? "karl-blink" : undefined}
          style={{ pointerEvents: "none" }}
        >
          {/* Wider than the device disc underneath, so it encircles the symbol
              rather than covering it — and hollow, so it can never be read as a
              device in its own right. */}
          <circle
            data-testid="selected-device-ring"
            cx={marker.x}
            cy={marker.y}
            r={size * 1.15}
            fill="none"
            stroke={SELECTED_DEVICE_STROKE}
            strokeWidth={size * 0.24}
          />
        </g>
      ))}
    </>
  );
};
